import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 3;
const MIN_HEIGHT = 150;

/**
 * A dropdown panel rendered at the end of the document instead of inside its
 * row. The medicine table scrolls sideways, and a container that scrolls on one
 * axis clips the other too - so a normal absolutely-positioned list gets cut
 * off. Portalling out and positioning with fixed coordinates avoids that
 * entirely, and the panel flips above the input when there is no room below.
 */
export default function FloatingPanel({ anchorRef, open, children, minWidth, className = '' }) {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setStyle(null);
      return undefined;
    }

    function place() {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;

      const below = window.innerHeight - anchor.bottom;
      const above = anchor.top;
      const flip = below < MIN_HEIGHT && above > below;
      const room = (flip ? above : below) - GAP - 8;

      setStyle({
        position: 'fixed',
        left: Math.max(8, Math.min(anchor.left, window.innerWidth - (minWidth || anchor.width) - 8)),
        width: anchor.width,
        minWidth,
        maxHeight: Math.max(MIN_HEIGHT, Math.min(250, room)),
        ...(flip
          ? { bottom: window.innerHeight - anchor.top + GAP }
          : { top: anchor.bottom + GAP }),
      });
    }

    place();
    // `true` catches scrolling of the table wrapper, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchorRef, minWidth]);

  if (!open || !style) return null;

  return createPortal(
    <div className={`suggest-list ${className}`} style={style} data-floating-panel>
      {children}
    </div>,
    document.body,
  );
}

/** True when the event happened inside any floating panel. */
export function isInsideFloatingPanel(target) {
  return !!target?.closest?.('[data-floating-panel]');
}
